import React, { useState } from 'react';
import { Bell, Sparkles, CheckCircle2, ShieldCheck } from 'lucide-react';

const PortalNotifications: React.FC = () => {
  const [preferences, setPreferences] = useState<{ [key: string]: boolean }>({
    n1: true,
    n2: true,
    n3: true,
    n4: true,
    n5: true,
  });
  const [saved, setSaved] = useState(false);

  const settings = [
    {
      id: 'n1',
      label: 'Monthly Statements & Official Tax Receipts',
      desc: 'Receive consolidated tax invoices and accounting PDFs instantly upon billing.',
      badge: 'Accounting',
    },
    {
      id: 'n2',
      label: 'Proactive Usage & Quota Velocity Alerts',
      desc: 'Get notified when your workspace hits 80% quota with 1-click upgrade and top-up recommendations to prevent downtime.',
      badge: 'Zero-Downtime',
    },
    {
      id: 'n3',
      label: 'Payment Security & Account Health Notices',
      desc: 'Instant gentle alerts if a payment method expires so you can seamlessly update without service disruption.',
      badge: 'Continuity',
    },
    {
      id: 'n4',
      label: 'Plan Optimization & Volume Savings Tips',
      desc: 'Receive quarterly AI analysis on how switching tiers or bundling workspace add-ons can reduce your monthly bill.',
      badge: 'Cost Savings',
    },
    {
      id: 'n5',
      label: 'New Feature & VIP Beta Quota Unlocks',
      desc: 'Early access invites and complimentary bonus compute quota for upcoming major platform releases.',
      badge: 'VIP Access',
    },
  ];

  const handleToggle = (id: string) => {
    setPreferences(prev => ({ ...prev, [id]: !prev[id] }));
  };

  const handleSave = () => {
    setSaved(true);
    setTimeout(() => setSaved(false), 3000);
  };

  return (
    <div className="bg-white rounded-[2.5rem] border border-slate-200 shadow-sm animate-in fade-in duration-500 overflow-hidden">
      <div className="p-8 sm:p-10 border-b border-slate-100 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <div className="flex items-center gap-2">
            <h3 className="text-xl font-black text-slate-900 tracking-tight uppercase">
              Billing & Optimization Notifications
            </h3>
            <span className="px-2.5 py-0.5 rounded-full text-[9px] font-black uppercase tracking-wider bg-indigo-50 text-indigo-600 border border-indigo-200">
              Smart Alerts
            </span>
          </div>
          <p className="text-xs text-slate-400 font-bold uppercase tracking-widest mt-1">
            Configure transaction updates, volume notifications, and account continuity alerts
          </p>
        </div>
        {saved && (
          <span className="text-xs font-bold text-emerald-600 bg-emerald-50 px-4 py-2 rounded-xl flex items-center gap-1.5 animate-in fade-in">
            <CheckCircle2 className="w-4 h-4" /> Preferences Saved!
          </span>
        )}
      </div>

      <div className="divide-y divide-slate-50">
        {settings.map(s => {
          const isChecked = !!preferences[s.id];
          return (
            <div key={s.id} className="p-8 sm:p-10 flex items-center justify-between hover:bg-slate-50/50 transition-colors gap-6">
              <div className="max-w-2xl space-y-1">
                <div className="flex items-center gap-2.5">
                  <p className="text-sm font-black text-slate-900">{s.label}</p>
                  <span className="px-2 py-0.5 rounded text-[8px] font-black uppercase tracking-wider bg-slate-100 text-slate-600">
                    {s.badge}
                  </span>
                </div>
                <p className="text-xs text-slate-500 font-medium leading-relaxed">{s.desc}</p>
              </div>
              <button
                type="button"
                onClick={() => handleToggle(s.id)}
                className={`w-14 h-7 rounded-full relative p-1 transition-all shrink-0 cursor-pointer ${
                  isChecked ? 'bg-indigo-600 shadow-md shadow-indigo-100' : 'bg-slate-200'
                }`}
                aria-label={`Toggle ${s.label}`}
              >
                <div
                  className={`w-5 h-5 bg-white rounded-full shadow-md transition-transform ${
                    isChecked ? 'translate-x-7' : 'translate-x-0'
                  }`}
                />
              </button>
            </div>
          );
        })}
      </div>

      <div className="p-8 sm:p-10 bg-slate-50/50 border-t border-slate-100 flex flex-col sm:flex-row items-center justify-between gap-4">
        <div className="text-[10px] font-bold uppercase text-slate-400 flex items-center gap-2">
          <ShieldCheck className="w-4 h-4 text-slate-500" />
          <span>Zero-spam policy • Instant 1-click unsubscribe anytime</span>
        </div>
        <button
          onClick={handleSave}
          className="w-full sm:w-auto px-10 py-4 bg-indigo-600 hover:bg-indigo-700 text-white rounded-2xl text-xs font-black uppercase tracking-widest shadow-xl transition-all cursor-pointer"
        >
          Apply Preferences
        </button>
      </div>
    </div>
  );
};

export default PortalNotifications;
